import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EndedAssignmentsListHeaderSortKeys } from '../../_enums/base-customer-header-sort-keys.enum';
import {
  AssignmentCustomerResponse,
  BASE_PERMISSIONS,
  baseAdministrationSelectors,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseButtonSize,
  BaseCoreState,
  BaseOrderDirectionEnum,
  BaseRolesService,
  CustomerProfileAssignmentItemResponse,
  CustomerResponse,
} from 'base';

@Component({
  selector: 'base-customer-employee-master-data-tab',
  templateUrl: './base-customer-employee-master-data-tab.component.html',
})
export class BaseCustomerEmployeeMasterDataTabComponent implements OnChanges, OnInit, OnDestroy {
  @Input() assignment: AssignmentCustomerResponse | undefined;

  endedAssignments: Array<CustomerProfileAssignmentItemResponse> = [];
  listHeaderSortKeys = EndedAssignmentsListHeaderSortKeys;
  orderDirection: BaseOrderDirectionEnum;
  orderKey: number;
  directions = BaseOrderDirectionEnum;
  buttonSizes = BaseButtonSize;
  canEditPersonalId: boolean;
  isInternalPersonalIdEdited = false;
  isInternalPersonalIdFormSubmitted = false;
  internalPersonalIdForm = this.fb.group({
    internalPersonalId: ['', Validators.required],
  });
  modalRef: NgbModalRef;
  isTerminalFeatureActive: boolean;
  private destroy$ = new Subject();

  constructor(
    private fb: NonNullableFormBuilder,
    private store: Store<BaseCoreState>,
    private rolesService: BaseRolesService
  ) {}

  ngOnChanges(): void {
    if (!!this.assignment) {
      this.isInternalPersonalIdEdited = false;
      this.internalPersonalIdForm.patchValue({
        internalPersonalId: this.assignment.profile?.customerProfilePersonalId || '',
      });
      this.store.dispatch(baseAssignmentsActions.getCandidateEndedAssignments({ payload: this.assignment.profile.id }));

      this.store
        .select(baseAssignmentsSelectors.getCandidateEndedAssignments)
        .pipe(
          takeUntil(this.destroy$),
          filter((endedAssignmentsState) => endedAssignmentsState.ok === true),
          map((endedAssignmentsState) => endedAssignmentsState.data)
        )
        .subscribe((endedAssignments) => {
          this.endedAssignments = endedAssignments || [];
        });
    }
  }

  ngOnInit(): void {
    this.canEditPersonalId = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENTS);
    this.subscribeCustomerData();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  updateInternalPersonalId(): void {
    this.isInternalPersonalIdFormSubmitted = true;
    if (this.internalPersonalIdForm.valid && this.canEditPersonalId) {
      if (!this.assignment) {
        return;
      }

      this.store.dispatch(
        baseAssignmentsActions.updateCustomerProfilePersonalId({
          payload: {
            profile: this.assignment.profile.id,
            assignment: this.assignment.id,
            body: {
              personalId: this.internalPersonalIdForm.controls.internalPersonalId.value,
            },
          },
        })
      );
      this.isInternalPersonalIdEdited = false;
      this.isInternalPersonalIdFormSubmitted = false;
    }
  }

  cancelUpdate() {
    this.isInternalPersonalIdEdited = false;
    this.isInternalPersonalIdFormSubmitted = false;
    this.internalPersonalIdForm.controls.internalPersonalId.setValue(
      this.assignment?.profile.customerProfilePersonalId || ''
    );
  }

  onOrderChanged(key: number): void {
    if (this.endedAssignments?.length) {
      this.orderKey = key;
      this.orderDirection =
        !this.orderDirection || this.orderDirection === this.directions.desc
          ? this.directions.asc
          : this.directions.desc;
      this.orderEndedAssignments();
    }
  }

  orderEndedAssignments(): void {
    switch (this.orderKey) {
      case EndedAssignmentsListHeaderSortKeys.period:
        this.endedAssignments = [...this.endedAssignments].sort((assignmentA, assignmentB) => {
          if (this.orderDirection === this.directions.asc) {
            return new Date(assignmentA.startDate!).getTime() - new Date(assignmentB.startDate!).getTime();
          } else {
            return new Date(assignmentB.startDate!).getTime() - new Date(assignmentA.startDate!).getTime();
          }
        });
        break;
      case EndedAssignmentsListHeaderSortKeys.department:
        this.endedAssignments = [...this.endedAssignments].sort((assignmentA, assignmentB) => {
          if (this.orderDirection === this.directions.asc) {
            return assignmentA.customerDepartment!.name.localeCompare(assignmentB.customerDepartment!.name);
          } else {
            return assignmentB.customerDepartment!.name.localeCompare(assignmentA.customerDepartment!.name);
          }
        });
        break;
      case EndedAssignmentsListHeaderSortKeys.qualification:
        this.endedAssignments = [...this.endedAssignments].sort((assignmentA, assignmentB) => {
          if (this.orderDirection === this.directions.asc) {
            return assignmentA.mainQualification!.name.localeCompare(assignmentB.mainQualification!.name);
          } else {
            return assignmentB.mainQualification!.name.localeCompare(assignmentA.mainQualification!.name);
          }
        });
        break;
    }
  }

  private subscribeCustomerData(): void {
    this.store
      .select(baseAdministrationSelectors.getCustomerData)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as CustomerResponse)
      )
      .subscribe((customer) => {
        this.isTerminalFeatureActive = customer.timeRecordTerminalUsage;
      });
  }
}
